// Player ship entity - VSF-7 "Tempest Lancer"
// Gravitar-style physics: rotation + thrust with planetary gravity

import { Entity } from "../core/Entity";
import { InputManager } from "../core/InputManager";
import { VectorRenderer } from "../graphics/VectorRenderer";
import { VectorShape, Vec2 } from "../utils/types";
import { clamp, degreesToRadians } from "../utils/math";
import { CONFIG } from "../config";

// Ship shape - sleek fighter design
const PLAYER_SHAPE: VectorShape = {
  points: [
    { x: 0, y: -15 }, // nose
    { x: -4, y: -8 },
    { x: -10, y: 5 }, // left wing tip
    { x: -4, y: 3 },
    { x: -6, y: 12 }, // left tail
    { x: -2, y: 8 },
    { x: 0, y: 15 }, // tail center
    { x: 2, y: 8 },
    { x: 6, y: 12 }, // right tail
    { x: 4, y: 3 },
    { x: 10, y: 5 }, // right wing tip
    { x: 4, y: -8 },
  ],
  closed: true,
};

// Thrust flame shapes - pre-allocated with mutable points for animation
const OUTER_FLAME_POINTS: Vec2[] = [
  { x: -3, y: 15 },
  { x: 0, y: 28 },
  { x: 3, y: 15 },
];
const OUTER_FLAME_SHAPE: VectorShape = { points: OUTER_FLAME_POINTS, closed: false };

const INNER_FLAME_POINTS: Vec2[] = [
  { x: -2, y: 15 },
  { x: 0, y: 22 },
  { x: 2, y: 15 },
];
const INNER_FLAME_SHAPE: VectorShape = { points: INNER_FLAME_POINTS, closed: false };

export class Player extends Entity {
  private input: InputManager;

  // Resources
  private fuel: number;
  private shieldEnergy: number;
  private bombs: number;

  // Physics (from config but can be modified per-zone)
  private gravity: number;
  private thrustPower: number;
  private rotationSpeed: number;

  // Weapon state
  private fireCooldown: number;
  private bombCooldown: number;

  // Visual feedback
  private shieldFlashTimer: number;
  private shieldBounceScale: number;
  private isThrusting: boolean;
  private thrustFlicker: number;

  // Invincibility after respawn
  private invincibleTimer: number;

  // Previous position for swept collision detection
  private previousPosition: Vec2;

  // Reusable collision points array to avoid allocations
  private collisionPointsCache: Vec2[] = [];

  // Callbacks
  private onFire?: () => void;
  private onBomb?: () => void;

  constructor(x: number, y: number, input: InputManager) {
    super(x, y);
    this.previousPosition = { x, y };
    this.shape = PLAYER_SHAPE;
    this.color = CONFIG.colors.player;
    this.input = input;

    // Pre-allocate collision points cache
    for (let i = 0; i < PLAYER_SHAPE.points.length; i++) {
      this.collisionPointsCache.push({ x: 0, y: 0 });
    }

    // Initialize from config
    this.fuel = CONFIG.player.startingFuel;
    this.shieldEnergy = CONFIG.player.startingShield;
    this.bombs = CONFIG.player.startingBombs;

    this.gravity =
      CONFIG.physics.baseGravity * CONFIG.physics.gravityMultipliers.zone1;
    this.thrustPower = CONFIG.player.thrustPower;
    this.rotationSpeed = degreesToRadians(CONFIG.player.rotationSpeed);

    this.fireCooldown = 0;
    this.bombCooldown = 0;

    this.shieldFlashTimer = 0;
    this.shieldBounceScale = 1;
    this.isThrusting = false;
    this.thrustFlicker = 0;

    this.invincibleTimer = 0;

    // Start facing up (nose pointing up, which is -Y in canvas coords)
    this.rotation = 0;
  }

  setOnFire(callback: () => void): void {
    this.onFire = callback;
  }

  setOnBomb(callback: () => void): void {
    this.onBomb = callback;
  }

  setGravity(gravity: number): void {
    this.gravity = gravity;
  }

  setInvincible(duration: number): void {
    this.invincibleTimer = duration;
  }

  isInvincible(): boolean {
    return this.invincibleTimer > 0;
  }

  update(dt: number): void {
    if (!this.active) return;

    // Convert frame-based values to time-based (config is tuned for 60fps)
    const frameRatio = dt * 60;

    // --- ROTATION ---
    if (this.input.isKeyDown("ArrowLeft")) {
      this.rotation -= this.rotationSpeed * frameRatio;
    }
    if (this.input.isKeyDown("ArrowRight")) {
      this.rotation += this.rotationSpeed * frameRatio;
    }

    // Normalize rotation to 0-2PI
    while (this.rotation < 0) this.rotation += Math.PI * 2;
    while (this.rotation >= Math.PI * 2) this.rotation -= Math.PI * 2;

    // --- GRAVITY ---
    // Gravity always pulls down (+Y in canvas coords)
    this.velocity.y += this.gravity * frameRatio;

    // --- THRUST ---
    // Thrust direction is where the ship's nose is pointing
    const thrustDir = {
      x: Math.sin(this.rotation),
      y: -Math.cos(this.rotation),
    };

    this.isThrusting = false;

    // Main thrust (up arrow)
    if (this.input.isKeyDown("ArrowUp") && this.fuel > 0) {
      this.velocity.x += thrustDir.x * this.thrustPower * frameRatio;
      this.velocity.y += thrustDir.y * this.thrustPower * frameRatio;
      this.fuel -= CONFIG.player.fuelConsumptionThrust * dt;
      this.isThrusting = true;
    }

    // Reverse thrust (down arrow) - half power per GDD
    if (this.input.isKeyDown("ArrowDown") && this.fuel > 0) {
      const reversePower =
        this.thrustPower * CONFIG.player.reverseThrustMultiplier;
      this.velocity.x -= thrustDir.x * reversePower * frameRatio;
      this.velocity.y -= thrustDir.y * reversePower * frameRatio;
      this.fuel -= CONFIG.player.fuelConsumptionThrust * dt * 0.5;
    }

    // --- DRAG ---
    // Very low drag (near-vacuum)
    const drag = 1 - CONFIG.physics.drag * frameRatio;
    this.velocity.x *= drag;
    this.velocity.y *= drag;

    // --- POSITION UPDATE ---
    // Save previous position for swept collision detection
    this.previousPosition.x = this.position.x;
    this.previousPosition.y = this.position.y;

    // No max speed cap - true Newtonian physics
    this.position.x += this.velocity.x * frameRatio;
    this.position.y += this.velocity.y * frameRatio;

    // --- WEAPON COOLDOWNS ---
    if (this.fireCooldown > 0) {
      this.fireCooldown -= dt;
    }
    if (this.bombCooldown > 0) {
      this.bombCooldown -= dt;
    }

    // --- FIRE WEAPONS ---
    if (this.input.isKeyDown("Space") && this.fireCooldown <= 0) {
      this.fire();
    }
    if (
      this.input.wasKeyPressed("KeyB") &&
      this.bombCooldown <= 0 &&
      this.bombs > 0
    ) {
      this.dropBomb();
    }

    // --- RESOURCE BOUNDS ---
    this.fuel = clamp(this.fuel, 0, 100);

    // --- SHIELD REGENERATION ---
    if (this.shieldEnergy < 100 && this.shieldEnergy > 0) {
      this.shieldEnergy += CONFIG.player.shieldRegenRate * dt;
      this.shieldEnergy = Math.min(this.shieldEnergy, 100);
    }

    // --- VISUAL TIMERS ---
    if (this.shieldFlashTimer > 0) {
      this.shieldFlashTimer -= dt;
    }
    if (this.shieldBounceScale > 1) {
      this.shieldBounceScale -= dt * 4;
      if (this.shieldBounceScale < 1) this.shieldBounceScale = 1;
    }
    if (this.invincibleTimer > 0) {
      this.invincibleTimer -= dt;
    }

    // Thrust flame flicker
    this.thrustFlicker = Math.random() * 8;
  }

  private fire(): void {
    this.fireCooldown = CONFIG.weapons.pulseFireRate;
    this.onFire?.();
  }

  private dropBomb(): void {
    if (this.bombs <= 0) return;
    this.bombs--;
    this.bombCooldown = CONFIG.weapons.bombCooldown;
    this.onBomb?.();
  }

  render(renderer: VectorRenderer): void {
    if (!this.active) return;

    // Flicker when invincible
    if (
      this.invincibleTimer > 0 &&
      Math.floor(this.invincibleTimer * 10) % 2 === 0
    ) {
      return; // Skip rendering every other frame for flicker effect
    }

    // --- SHIELD --- (only visible when hit)
    if (this.shieldEnergy > 0 && this.shieldFlashTimer > 0) {
      const baseRadius = 25;
      const shieldRadius = baseRadius * this.shieldBounceScale;
      const energyRatio = this.shieldEnergy / 100;

      let shieldColor: string;
      if (energyRatio > 0.6) {
        shieldColor = CONFIG.colors.shield;
      } else if (energyRatio > 0.3) {
        shieldColor = "#FFFF00"; // Yellow warning
      } else {
        shieldColor = "#FF8800"; // Orange critical
      }

      const intensity = this.shieldFlashTimer / 0.3;
      renderer.setGlow(15 + intensity * 15, "#FFFFFF");
      renderer.drawCircle(this.position, shieldRadius, 20, "#FFFFFF");
      renderer.drawCircle(this.position, shieldRadius * 0.8, 12, shieldColor);
      renderer.clearGlow();
    }

    // --- SHIP ---
    renderer.setGlow(CONFIG.display.glowIntensity, this.color);
    super.render(renderer);

    // --- THRUST FLAME ---
    if (this.isThrusting && this.fuel > 0) {
      // Mutate pre-allocated flame points for flicker (no allocations)
      OUTER_FLAME_POINTS[1].y = 28 + this.thrustFlicker;
      renderer.drawShape(
        OUTER_FLAME_SHAPE,
        this.position,
        this.rotation,
        this.scale,
        "#FFFF00",
      );

      // Inner flame
      INNER_FLAME_POINTS[1].y = 22 + this.thrustFlicker * 0.5;
      renderer.drawShape(
        INNER_FLAME_SHAPE,
        this.position,
        this.rotation,
        this.scale,
        "#FFFFFF",
      );
    }

    renderer.clearGlow();
  }

  // --- GETTERS ---

  getFuel(): number {
    return Math.floor(this.fuel);
  }

  getShieldEnergy(): number {
    return Math.floor(this.shieldEnergy);
  }

  getBombs(): number {
    return this.bombs;
  }

  getSpeed(): number {
    return Math.sqrt(this.velocity.x ** 2 + this.velocity.y ** 2);
  }

  getCollisionRadius(): number {
    return CONFIG.player.collisionRadius;
  }

  // Get the nose position for bullets
  getGunPosition(): { x: number; y: number; angle: number } {
    const gunOffset = 15;
    return {
      x: this.position.x + Math.sin(this.rotation) * gunOffset,
      y: this.position.y - Math.cos(this.rotation) * gunOffset,
      angle: this.rotation,
    };
  }

  getBombPosition(): { x: number; y: number; vx: number; vy: number } {
    return {
      x: this.position.x,
      y: this.position.y,
      vx: this.velocity.x * 0.5,
      vy: this.velocity.y * 0.5,
    };
  }

  // --- RESOURCE MODIFIERS ---

  addFuel(amount: number): void {
    this.fuel = clamp(this.fuel + amount, 0, 100);
  }

  addShieldEnergy(amount: number): void {
    this.shieldEnergy = clamp(this.shieldEnergy + amount, 0, 100);
  }

  addBomb(amount: number = 1): void {
    this.bombs += amount;
  }

  // --- DAMAGE ---

  hasShield(): boolean {
    return this.shieldEnergy > 0;
  }

  // Returns true if shield absorbed the hit
  takeDamage(damage: number = 15): boolean {
    if (this.invincibleTimer > 0) return true;

    if (this.shieldEnergy > 0) {
      this.shieldEnergy -= damage;
      this.shieldFlashTimer = 0.3;
      this.shieldBounceScale = 1.4;

      if (this.shieldEnergy < 0) {
        this.shieldEnergy = 0;
      }
      return true; // Shield absorbed
    }
    return false; // No shield, fatal hit
  }

  // --- COLLISION POINTS ---
  // Returns transformed points for precise terrain collision
  // Uses cached array to avoid allocations every frame
  getCollisionPoints(): Vec2[] {
    const cos = Math.cos(this.rotation);
    const sin = Math.sin(this.rotation);
    const points = this.shape.points;
    const posX = this.position.x;
    const posY = this.position.y;
    const scale = this.scale;

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      this.collisionPointsCache[i].x = posX + (p.x * cos - p.y * sin) * scale;
      this.collisionPointsCache[i].y = posY + (p.x * sin + p.y * cos) * scale;
    }

    return this.collisionPointsCache;
  }

  // Returns the position from before the last update (for swept collision)
  // Note: Returns reference to internal object - do not modify!
  getPreviousPosition(): Vec2 {
    return this.previousPosition;
  }

  // --- BOUNCE ---
  // Physically correct bounce off terrain using reflection
  // normal: surface normal (unit vector pointing away from surface)
  bounce(normalX: number, normalY: number): void {
    const bounceDamping = 0.7; // Lose some energy on bounce

    // Reflection formula: v' = v - 2(v · n)n
    // Where v is velocity, n is surface normal
    const dotProduct = this.velocity.x * normalX + this.velocity.y * normalY;

    // Only bounce if moving into the surface (dot product negative)
    if (dotProduct < 0) {
      this.velocity.x =
        (this.velocity.x - 2 * dotProduct * normalX) * bounceDamping;
      this.velocity.y =
        (this.velocity.y - 2 * dotProduct * normalY) * bounceDamping;
    }

    // Push away from collision point along normal
    const pushStrength = 8;
    this.position.x += normalX * pushStrength;
    this.position.y += normalY * pushStrength;
  }
}
